/**
 * MapaPage (rc102) — módulo Mapa (Operación): clientes/contratos sobre OSM.
 *
 * Filtros en cascada como lo pidió Alex: EMPRESA primero, luego FUENTE
 * (ubicación del contrato — rc101 — o del cliente) y estatus. Pins
 * coloreados por estatus con popup → ficha. Tiles calle o satélite
 * (mismas capas que el LocationPicker de prospectos).
 */
import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Loader2 } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useEmpresasMap } from "@/features/empresas/useEmpresasMap";

import { useMapaUbicaciones, type PinUbicacion } from "./api";

const DEFAULT_CENTER: [number, number] = [20.27, -98.15]; // zona Pahuatlán
const DEFAULT_ZOOM = 11;

// Colores por estatus (hex directos — leaflet no lee CSS vars)
const ESTATUS_COLOR: Record<string, string> = {
  activo: "#2563eb",      // zafiro
  suspendido: "#f59e0b",  // warn
  error: "#ef4444",       // danger
  pendiente: "#9ca3af",   // muted
  cancelado: "#6b7280",
};

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points)]);
  return null;
}

export function MapaPage() {
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [fuente, setFuente] = useState<"contratos" | "clientes">("contratos");
  const [estatus, setEstatus] = useState("");
  // rc105 — "hybrid" = satélite Esri + capas de referencia (nombres + vías)
  const [tiles, setTiles] = useState<"street" | "satellite" | "hybrid">("street");

  const { empresas } = useEmpresasMap();
  const { data, isLoading } = useMapaUbicaciones({ fuente, empresaId, estatus });
  const items = data?.items ?? [];

  const points = useMemo(
    () => items.map((p) => [p.lat, p.lng] as [number, number]),
    [items],
  );
  const porEstatus = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of items) m[p.estatus] = (m[p.estatus] ?? 0) + 1;
    return m;
  }, [items]);

  return (
    <div className="flex flex-col gap-4">
      {/* Header + filtros */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MapPin size={16} /> Mapa
          </h2>
          <p className="text-xs text-muted-foreground">
            {isLoading ? "Cargando…" : `${items.length} ubicaciones`}
            {fuente === "contratos" ? " · por contrato (instalación)" : " · por cliente (domicilio)"}
          </p>
        </div>
        {/* rc140 móvil: selects a la mitad, toggle de capas a lo ancho */}
        <div className="flex flex-wrap items-end gap-2 max-md:w-full max-md:[&>div]:min-w-[46%] max-md:[&>div]:flex-1 max-md:[&_select]:w-full max-md:[&_select]:min-w-0">
          <div>
            <Label className="text-[10px]">Empresa</Label>
            <Select
              value={empresaId === null ? "" : String(empresaId)}
              onChange={(e) => setEmpresaId(e.target.value === "" ? null : Number(e.target.value))}
              className="text-xs min-w-[170px]"
            >
              <option value="">Todas</option>
              {empresas.map((em) => (
                <option key={em.id} value={em.id}>{em.nombre}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Fuente</Label>
            <Select value={fuente}
              onChange={(e) => setFuente(e.target.value as typeof fuente)}
              className="text-xs">
              <option value="contratos">Ubicación del contrato</option>
              <option value="clientes">Ubicación del cliente</option>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Estatus</Label>
            <Select value={estatus}
              onChange={(e) => setEstatus(e.target.value)}
              className="text-xs">
              <option value="">Todos (sin cancelados)</option>
              <option value="activo">Activo</option>
              <option value="suspendido">Suspendido</option>
              <option value="error">Error</option>
              <option value="pendiente">Pendiente</option>
              <option value="cancelado">Cancelado</option>
            </Select>
          </div>
          <div className="inline-flex rounded-md border border-border bg-card overflow-hidden">
            {(["street", "satellite", "hybrid"] as const).map((t) => (
              <button key={t} type="button" onClick={() => setTiles(t)}
                className={cn(
                  "font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 transition-colors",
                  t === tiles ? "bg-electric text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/40",
                )}>
                {t === "street" ? "Calle" : t === "satellite" ? "Satélite" : "Híbrido"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Leyenda por estatus */}
      <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] text-muted-foreground">
        {Object.entries(ESTATUS_COLOR).map(([est, color]) =>
          (porEstatus[est] ?? 0) > 0 ? (
            <span key={est} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full border border-white/30"
                style={{ backgroundColor: color }} />
              {est} ({porEstatus[est]})
            </span>
          ) : null,
        )}
      </div>

      {/* Mapa */}
      {/* rc140 móvil: el mapa ocupa lo que queda de pantalla (menos barra inferior) */}
      <Card className="rounded-xl overflow-hidden shadow-sm max-md:[--mapa-h:calc(100dvh-20rem)]">
        {isLoading && items.length === 0 ? (
          <div className="flex h-[70vh] items-center justify-center text-xs text-muted-foreground">
            <Loader2 size={18} className="animate-spin mr-2" /> cargando mapa…
          </div>
        ) : (
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            style={{ height: "var(--mapa-h, 70vh)", width: "100%" }}
            scrollWheelZoom
          >
            {tiles === "street" ? (
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            ) : (
              <>
                <TileLayer
                  attribution="Tiles &copy; Esri"
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />
                {/* rc105 — Híbrido: satélite + overlays de referencia Esri
                    (vías + nombres de lugares) encima de la imagen */}
                {tiles === "hybrid" && (
                  <>
                    <TileLayer
                      attribution=""
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                      maxZoom={19}
                    />
                    <TileLayer
                      attribution=""
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                      maxZoom={19}
                    />
                  </>
                )}
              </>
            )}
            <FitBounds points={points} />
            {items.map((p) => (
              <CircleMarker
                key={`${p.tipo}-${p.id}`}
                center={[p.lat, p.lng]}
                radius={7}
                pathOptions={{
                  color: "#ffffff",
                  weight: 1.5,
                  fillColor: ESTATUS_COLOR[p.estatus] ?? "#6b7280",
                  fillOpacity: 0.9,
                }}
              >
                <Popup>
                  <PinPopup pin={p} />
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </Card>
    </div>
  );
}

function PinPopup({ pin }: { pin: PinUbicacion }) {
  return (
    <div className="text-xs min-w-[180px]">
      <div className="font-semibold">{pin.cliente_nombre}</div>
      <div className="font-mono text-[10px] text-muted-foreground mb-1">
        {pin.codigo}{pin.plan_nombre ? ` · ${pin.plan_nombre}` : ""} · {pin.estatus}
      </div>
      {pin.referencia && <div className="mb-1 italic">{pin.referencia}</div>}
      <div className="flex flex-col gap-0.5">
        {pin.tipo === "contrato" ? (
          <Link to={`/contratos/${pin.id}`} className="text-primary hover:underline">
            Abrir contrato →
          </Link>
        ) : null}
        <Link to={`/clientes/${pin.cliente_id}`} className="text-primary hover:underline">
          Abrir cliente →
        </Link>
        <a
          href={`https://maps.google.com/?q=${pin.lat},${pin.lng}`}
          target="_blank" rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Google Maps →
        </a>
      </div>
    </div>
  );
}
